import { CANVAS_TOOLS } from "../nativeCanvasElements";
import {
  getStageCenterScreenPoint,
  screenToScene,
} from "../geometry/viewportTransform";
import { prepareElementsForPaste } from "./clipboardOperations";
import {
  applyStyleToElements,
  deleteElements,
  duplicateElements,
  moveElementsBy,
} from "./sceneOperations";

/**
 * Engine actions that operate on the current selection: styling, deletion,
 * duplication, nudging, select-all and clipboard paste. Extracted from the
 * engine facade; mutates engine state through the provided internals only.
 * @param {{ commitElements: Function, defaultTool?: string, setSelectedIds: Function, setTool: Function, state: object }} params
 * @returns {object}
 */
export const createSelectionActions = ({
  commitElements,
  defaultTool = CANVAS_TOOLS.SELECT,
  setSelectedIds,
  setTool,
  state,
}) => {
  const applyStyle = (style) => {
    if (!state.selectedIds.size) return;
    commitElements(
      applyStyleToElements({
        elements: state.elements,
        ids: state.selectedIds,
        style,
      })
    );
  };

  const deleteSelected = () => {
    if (!state.selectedIds.size) return;
    state.editingText = null;
    commitElements(
      deleteElements({ elements: state.elements, ids: state.selectedIds }),
      { selectIds: new Set() }
    );
  };

  const duplicateSelected = () => {
    if (!state.selectedIds.size) return;
    const duplicated = duplicateElements({
      elements: state.elements,
      ids: state.selectedIds,
    });
    commitElements(duplicated.elements, {
      selectIds: new Set(duplicated.newIds),
    });
  };

  const getSelectedElements = () =>
    state.elements.filter((element) => state.selectedIds.has(element.id));

  const nudgeSelected = (delta) => {
    if (!state.selectedIds.size) return;
    commitElements(
      moveElementsBy({ delta, elements: state.elements, ids: state.selectedIds })
    );
  };

  const pasteElements = (clipboardElements) => {
    if (!clipboardElements?.length) return;
    const targetPoint =
      state.lastPointerScenePoint ??
      screenToScene({
        point: getStageCenterScreenPoint(state.size),
        viewport: state.viewport,
      });
    const pastedElements = prepareElementsForPaste({
      elements: clipboardElements,
      targetPoint,
    });
    if (!pastedElements.length) return;
    commitElements([...state.elements, ...pastedElements], {
      selectIds: new Set(pastedElements.map((element) => element.id)),
    });
    setTool(defaultTool);
  };

  const selectAll = () =>
    setSelectedIds(new Set(state.elements.map((element) => element.id)));

  return {
    applyStyle,
    deleteSelected,
    duplicateSelected,
    getSelectedElements,
    nudgeSelected,
    pasteElements,
    selectAll,
  };
};
